<script setup>
import Layout from "../../layouts/main.vue";
import mushroom from "cem-probe-api";
import { useRoute, useRouter } from "vue-router";
import { ref, reactive, onMounted, computed } from "vue";
import { FormInstance } from "element-plus";
import Swal from "sweetalert2";
import toastr from "toastr";
import MethodService from "../../service/MethodService";
import { useI18n } from "vue-i18n";
import Data from "./dataCustomerGroup";
import { Filter, Project } from "mushroomjs";
import { getCurrentPid } from "@/utils/systemType";

const { t } = useI18n();
const ruleFormRef = ref(FormInstance);
const dataForm = reactive({
  value: { name: "", description: "" },
});
const route = useRoute();
const routerPush = useRouter();
const validForm = reactive({ value: Data.validForm });
const loading = ref(false);

// Bảng khách hàng trong nhóm
const dataTables = ref([]);
const searchCustomerInGroup = ref("");
const searchProfileInGroup = ref("");
const searchVlanInGroup = ref("");
const searchMacInGroup = ref("");
const tableRules = reactive({
  page: 1,
  limit: 10,
  total: 0,
  offset: 0,
  lengthMenu: [10, 20, 50, 100],
});

// Options cho dropdown tìm kiếm (lấy từ API)
const profileOptions = ref([]);
const vlanOptions = ref([]);
const macOptions = ref([]);
const macSearchText = ref("");
const macLoading = ref(false);
let macDebounceTimer = null;

const fetchProfilesInGroup = async () => {
  try {
    const res = await mushroom.nhom_khach_hang.getProfilesInGroupAsync({
      body: {
        id_nhom: Number(route.params.id),
        probe_id: getCurrentPid(),
      },
    });
    const data = res.result?.profiles ?? [];
    profileOptions.value = [...data].sort((a, b) => a.localeCompare(b));
    console.log("profileOptions.value", profileOptions.value);
  } catch (e) {
    console.error("Có lỗi: %o", e);
    profileOptions.value = [];
  }
};

const fetchVlansInGroup = async () => {
  try {
    const res = await mushroom.nhom_khach_hang.getVlansInGroupAsync({
      body: {
        id_nhom: Number(route.params.id),
        probe_id: getCurrentPid(),
      },
    });
    const data = res.result?.vlans ?? [];
    vlanOptions.value = [...data].sort((a, b) => a - b);
  } catch (e) {
    console.error("Có lỗi: %o", e);
    vlanOptions.value = [];
  }
};

const fetchMacsInGroup = async (search = "") => {
  try {
    const body = {
      id_nhom: Number(route.params.id),
      probe_id: getCurrentPid(),
    };
    if (search) {
      body.mac = search;
      macLoading.value = true;
    }
    const res = await mushroom.nhom_khach_hang.getMacsInGroupAsync({
      body,
    });
    const data = res.result?.macs ?? [];
    macOptions.value = [...data].sort((a, b) => a.localeCompare(b));
  } catch (e) {
    console.error("Có lỗi: %o", e);
    macOptions.value = [];
  } finally {
    macLoading.value = false;
  }
};

const onMacFilterChange = (val) => {
  macSearchText.value = val;
  if (macDebounceTimer) clearTimeout(macDebounceTimer);
  if (!val) {
    fetchMacsInGroup();
    return;
  }
  macLoading.value = true;
  macDebounceTimer = setTimeout(() => {
    fetchMacsInGroup(val);
  }, 3000);
};

const fetchAllDistinctOptions = async () => {
  await Promise.all([
    fetchProfilesInGroup(),
    fetchVlansInGroup(),
    fetchMacsInGroup(),
  ]);
};

// Dialog thêm khách hàng
const showCustomerPopup = ref(false);
const allCustomers = ref([]);
const selectedCustomerIds = ref([]);
const searchAllCustomer = ref("");
const searchProfileName = ref("");
const searchVlan = ref("");
const searchMac = ref("");
const popupLoading = ref(false);
const popupTableRules = reactive({
  page: 1,
  limit: 10,
  total: 0,
  offset: 0,
  lengthMenu: [10, 20, 50, 100],
});

// Options cho dropdown tìm kiếm trong popup (lấy từ API riêng)
const popupProfileOptions = ref([]);
const popupVlanOptions = ref([]);
const popupMacOptions = ref([]);
const popupMacLoading = ref(false);
let popupMacDebounceTimer = null;

const fetchPopupProfiles = async () => {
  try {
    const res = await mushroom.nhom_khach_hang.getProfilesNotInGroupAsync({
      body: {
        id_nhom: Number(route.params.id),
        probe_id: getCurrentPid(),
      },
    });
    const data = res.result?.profiles ?? [];
    popupProfileOptions.value = [...data].sort((a, b) => a.localeCompare(b));
  } catch (e) {
    console.error("Có lỗi: %o", e);
    popupProfileOptions.value = [];
  }
};

const fetchPopupVlans = async () => {
  try {
    const res = await mushroom.nhom_khach_hang.getVlansNotInGroupAsync({
      body: {
        id_nhom: Number(route.params.id),
        probe_id: getCurrentPid(),
      },
    });
    const data = res.result?.vlans ?? [];
    popupVlanOptions.value = [...data].sort((a, b) => a - b);
  } catch (e) {
    console.error("Có lỗi: %o", e);
    popupVlanOptions.value = [];
  }
};

const fetchPopupMacs = async (search = "") => {
  popupMacLoading.value = true;
  try {
    const body = {
      id_nhom: Number(route.params.id),
      probe_id: getCurrentPid(),
    };
    if (search) {
      body.mac = search;
    }
    const res = await mushroom.nhom_khach_hang.getMacsNotInGroupAsync({
      body,
    });
    const data = res.result?.macs ?? [];
    popupMacOptions.value = [...data].sort((a, b) => a.localeCompare(b));
  } catch (e) {
    console.error("Có lỗi: %o", e);
    popupMacOptions.value = [];
  } finally {
    popupMacLoading.value = false;
  }
};

const onPopupMacFilterChange = (val) => {
  if (popupMacDebounceTimer) clearTimeout(popupMacDebounceTimer);
  if (!val) {
    fetchPopupMacs();
    return;
  }
  popupMacLoading.value = true;
  popupMacDebounceTimer = setTimeout(() => {
    fetchPopupMacs(val);
  }, 3000);
};

const fetchAllPopupDistinctOptions = async () => {
  await Promise.all([
    fetchPopupProfiles(),
    fetchPopupVlans(),
    fetchPopupMacs(),
  ]);
};

// --- Lấy thông tin nhóm khách hàng ---
const fetchGroupById = async () => {
  try {
    const res = await mushroom.nhom_khach_hang.listAsync(
      {
        fields: Project.include("id", "id_nhom", "name", "description").build(),
        filters: Filter.eq("id_nhom", route.params.id).build(),
      },
      {
        extra: {
          headers: {
            ProbeId: getCurrentPid(),
          },
          params: {
            _limit: 1,
          },
        },
      },
    );
    if (res.result) {
      dataForm.value.name = res.result[0]?.name || "";
      dataForm.value.description = res.result[0]?.description || "";
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
    MethodService.showError(e.code);
  }
};

// --- Lấy danh sách khách hàng trong nhóm ---
const fetchCustomersInGroup = async () => {
  loading.value = true;
  try {
    const params = {
      id_nhom: route.params.id,
      _limit: tableRules.limit,
      _offset: tableRules.offset,
      probe_id: getCurrentPid(),
    };
    if (searchCustomerInGroup.value) {
      const textSearch = searchCustomerInGroup.value + "";
      params.account = textSearch.trim();
    }
    params.account = searchCustomerInGroup.value?.trim();
    if (searchProfileInGroup.value) {
      const textSearch = searchProfileInGroup.value + "";
      params.profile_name = textSearch.trim();
    }
    if (searchVlanInGroup.value) {
      const textSearch = searchVlanInGroup.value + "";
      params.vlan_id_outer = Number(textSearch.trim());
    }
    if (searchMacInGroup.value) {
      const textSearch = searchMacInGroup.value + "";
      params.client_mac = textSearch.trim();
    }

    const res = await mushroom.nhom_khach_hang.getCustomersInGroupAsync(
      {
        body: params,
      },
      { enabledCache: false },
    );
    dataTables.value = res.result?.data ?? [];
    tableRules.total = res.result?.meta?.total ?? 0;
  } catch (e) {
    console.error("Có lỗi: %o", e);
    dataTables.value = [];
    tableRules.total = 0;
  } finally {
    loading.value = false;
  }
};

const fn_searchCustomerInGroup = () => {
  tableRules.page = 1;
  tableRules.offset = 0;
  fetchCustomersInGroup();
};
const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  fetchCustomersInGroup();
};
const fn_tableCurentChange = (page) => {
  tableRules.page = page;
  tableRules.offset = (page - 1) * tableRules.limit;
  fetchCustomersInGroup();
};

// --- Thêm / Cập nhật nhóm ---
const addGroup = (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    try {
      const res = await mushroom.nhom_khach_hang.createAsync(
        {
          name: dataForm.value.name.trim(),
          description: dataForm.value.description?.trim() || undefined,
          // probe_id: localStorage.getItem("pid"),
        },
        {
          extra: {
            headers: {
              ProbeId: getCurrentPid(),
            },
          },
        },
      );
      if (res.result) {
        toastr.success(t("toastr_add_success"));
        routerPush.push({ name: "CustomersGroup" });
      }
    } catch (e) {
      console.error("Có lỗi: %o", e);
      if (e?.code === 409 && e?.subCode === 1005) {
        toastr.error(t("t_customer_already_in_group"));
      } else if (e?.code === 400 && e?.subCode === 1002) {
        toastr.error(t("t_name_already_exists"));
      } else MethodService.showError(e.code);
    }
  });
};

const updateGroup = (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    try {
      // Cập nhật thông tin nhóm
      await mushroom.nhom_khach_hang.partialUpdateAsync(
        {
          id: route.params.id,
          name: dataForm.value.name.trim(),
          description: dataForm.value.description?.trim() || undefined,
          // probe_id: localStorage.getItem("pid"),
        },
        {
          extra: {
            headers: {
              ProbeId: getCurrentPid(),
            },
          },
        },
      );

      // Thêm/xóa khách hàng đã được xử lý trực tiếp qua dialog và nút xóa

      toastr.success(t("toastr_update_success"));
      await fetchCustomersInGroup();
      await fetchAllDistinctOptions();
    } catch (e) {
      console.error("Có lỗi: %o", e);
      if (e?.code === 409 && e?.subCode === 1005) {
        toastr.error(t("t_customer_already_in_group"));
      } else if (e?.code === 400 && e?.subCode === 1002) {
        toastr.error(t("t_name_already_exists"));
      } else MethodService.showError(e.code);
    }
  });
};

// --- Xóa khách hàng khỏi nhóm ---
const handleRemoveCustomer = (row) => {
  Swal.fire({
    title: t("t_warning"),
    text: t("t_delete_customer"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    allowOutsideClick: false,
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const item = { client_mac: row["client_mac"] };
        if (row["account"]) item.account = row["account"];
        if (row["vlan_id_outer"])
          item.vlan_id_outer = Number(row["vlan_id_outer"]);
        const res = await mushroom.nhom_khach_hang.removeCustomerFromGroupAsync(
          {
            body: {
              group_id: Number(route.params.id),
              customers: [item],
              probe_id: getCurrentPid(),
            },
          },
        );
        const result = res.result;
        if (result?.errors?.length > 0) {
          const errorList = result.errors
            .map((err) => `- ${err.error}`)
            .join("<br>");
          Swal.fire({
            title: t("swal_noti"),
            html: `${t("t_error_list")}:<br>${errorList}`,
            icon: "warning",
            confirmButtonText: t("swal_accept"),
          });
        } else {
          toastr.success(t("t_remove_customer_success"));
        }
        await fetchCustomersInGroup();
        await fetchAllDistinctOptions();
      } catch (e) {
        console.error("Có lỗi: %o", e);
        MethodService.showError(e.code);
      }
    }
  });
};

// --- Xóa hết khách hàng khỏi nhóm ---
const handleRemoveAllCustomers = () => {
  Swal.fire({
    title: t("t_warning"),
    text: t("t_delete_all_customers"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    allowOutsideClick: false,
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const res = await mushroom.nhom_khach_hang.clearCustomersInGroupAsync({
          body: {
            group_id: Number(route.params.id),
            probe_id: getCurrentPid(),
          },
        });
        const result = res.result;
        if (result?.errors?.length > 0) {
          const errorList = result.errors
            .map((err) => `- ${err.error}`)
            .join("<br>");
          Swal.fire({
            title: t("swal_noti"),
            html: `${t("t_error_list")}:<br>${errorList}`,
            icon: "warning",
            confirmButtonText: t("swal_accept"),
          });
        } else {
          toastr.success(t("t_remove_customer_success"));
        }
        await fetchCustomersInGroup();
        await fetchAllDistinctOptions();
      } catch (e) {
        console.error("Có lỗi: %o", e);
        MethodService.showError(e.code);
      }
    }
  });
};

// --- Dialog: Lấy danh sách tất cả khách hàng ---
const openAddCustomerPopup = () => {
  showCustomerPopup.value = true;
  selectedCustomerIds.value = [];
  selectedCustomersMap.value = new Map();
  popupTableRules.page = 1;
  popupTableRules.offset = 0;
  searchAllCustomer.value = "";
  searchProfileName.value = "";
  searchVlan.value = "";
  searchMac.value = "";
  fetchAllPopupDistinctOptions();
  fetchAllCustomers();
};

const fetchAllCustomers = async () => {
  popupLoading.value = true;
  try {
    const params = {
      id_nhom: route.params.id,
      _limit: popupTableRules.limit,
      _offset: popupTableRules.offset,
      probe_id: getCurrentPid(),
    };
    if (searchAllCustomer.value) {
      params.account = searchAllCustomer.value.trim();
    }
    if (searchProfileName.value) {
      params.profile_name = searchProfileName.value.trim();
    }
    if (searchVlan.value) {
      params.vlan_id_outer = Number(searchVlan.value);
    }
    if (searchMac.value) {
      params.client_mac = searchMac.value.trim();
    }

    const res = await mushroom.nhom_khach_hang.getCustomersNotInGroupAsync(
      { body: params },
      { enabledCache: false },
    );
    const data = res.result?.data ?? [];
    allCustomers.value = data;
    popupTableRules.total = res.result?.meta?.total ?? 0;
  } catch (e) {
    console.error("Có lỗi: %o", e);
    allCustomers.value = [];
    popupTableRules.total = 0;
  } finally {
    popupLoading.value = false;
  }
};

const fn_searchPopup = () => {
  popupTableRules.page = 1;
  popupTableRules.offset = 0;
  fetchAllCustomers();
};
const fn_popupSizeChange = (limit) => {
  popupTableRules.limit = limit;
  popupTableRules.page = 1;
  popupTableRules.offset = 0;
  fetchAllCustomers();
};
const fn_popupPageChange = (page) => {
  popupTableRules.page = page;
  popupTableRules.offset = (page - 1) * popupTableRules.limit;
  fetchAllCustomers();
};

// Lưu thông tin chi tiết khách hàng đã chọn (cross-page)
const selectedCustomersMap = ref(new Map());

const toggleCustomer = (row) => {
  const mac = row.client_mac;
  const idx = selectedCustomerIds.value.indexOf(mac);
  if (idx > -1) {
    selectedCustomerIds.value.splice(idx, 1);
    selectedCustomersMap.value.delete(mac);
  } else {
    selectedCustomerIds.value.push(mac);
    selectedCustomersMap.value.set(mac, row);
  }
};

const selectAllCustomers = () => {
  for (const c of allCustomers.value) {
    if (!selectedCustomerIds.value.includes(c.client_mac)) {
      selectedCustomerIds.value.push(c.client_mac);
      selectedCustomersMap.value.set(c.client_mac, c);
    }
  }
};

const isAllSelected = computed(() => {
  if (allCustomers.value.length === 0) return false;
  return allCustomers.value.every((c) =>
    selectedCustomerIds.value.includes(c.client_mac),
  );
});

const confirmAddCustomers = () => {
  if (selectedCustomerIds.value.length === 0) {
    toastr.warning(t("toastr_please_select_customer"));
    return;
  }

  Swal.fire({
    title: t("swal_noti"),
    text: t("t_confirm_add_customer"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    allowOutsideClick: false,
  }).then(async (result) => {
    if (result.isConfirmed) {
      const customers = selectedCustomerIds.value.map((mac) => {
        const c = selectedCustomersMap.value.get(mac) || {};
        const item = { client_mac: mac };
        if (c.account) item.account = c.account;
        if (c.vlan_id_outer) item.vlan_id_outer = Number(c.vlan_id_outer);
        return item;
      });

      if (customers.length === 0) {
        toastr.warning(t("t_customer_already_in_group"));
        return;
      }

      try {
        const res = await mushroom.nhom_khach_hang.addCustomerToGroupAsync({
          body: {
            group_id: Number(route.params.id),
            customers,
            probe_id: getCurrentPid(),
          },
        });
        const result = res.result;
        if (result?.errors?.length > 0) {
          const errorList = result.errors
            .map((err) => `- ${t("t-stt")} ${err.index + 1}: ${err.error}`)
            .join("<br>");
          Swal.fire({
            title: t("swal_noti"),
            html: `${t("t_error_list")}:<br>${errorList}`,
            icon: "warning",
            confirmButtonText: t("swal_accept"),
          });
        } else {
          toastr.success(t("t_add_customer_success"));
        }
        showCustomerPopup.value = false;
        await fetchCustomersInGroup();
        await fetchAllDistinctOptions();
      } catch (e) {
        console.error("Có lỗi: %o", e);
        if (e?.code === 409 && e?.subCode === 1005) {
          toastr.error(t("t_customer_already_in_group"));
        } else MethodService.showError(e.code);
      }
    }
  });
};

const isUpdateMode = () => route.name === "CustomersGroupUpdate";

onMounted(async () => {
  if (isUpdateMode()) {
    await fetchGroupById();
    await fetchAllDistinctOptions();
    await fetchCustomersInGroup();
  }
});
</script>

<template>
  <Layout>
    <!-- Form thông tin nhóm -->
    <div class="card">
      <div class="card-header">
        <h3 v-if="!isUpdateMode()">{{ $t("t-add") }}</h3>
        <h3 v-else>{{ $t("t-update") }}</h3>
      </div>
      <div class="card-body">
        <el-form
          ref="ruleFormRef"
          :model="dataForm.value"
          :rules="validForm.value"
          status-icon
          label-width="150px"
          label-position="left"
        >
          <el-form-item :label="$t('t_customer_group')" prop="name">
            <el-input
              v-model="dataForm.value.name"
              :placeholder="$t('t_customer_group_name')"
            />
          </el-form-item>
          <el-form-item :label="$t('t_description')" prop="description">
            <el-input
              type="textarea"
              :rows="3"
              v-model="dataForm.value.description"
              :placeholder="$t('t_description')"
            />
          </el-form-item>
          <div class="text-center">
            <button
              type="button"
              class="btn btn-soft-secondary btn-border mx-3"
              @click="routerPush.go(-1)"
            >
              {{ $t("t-back") }}
            </button>
            <button
              v-if="!isUpdateMode()"
              type="button"
              class="btn btn-secondary btn-border"
              @click="addGroup(ruleFormRef)"
            >
              {{ $t("t-add") }}
            </button>
            <button
              v-else
              type="button"
              class="btn btn-secondary btn-border"
              @click="updateGroup(ruleFormRef)"
            >
              {{ $t("t-update") }}
            </button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- Danh sách khách hàng trong nhóm -->
    <div class="card" v-if="isUpdateMode()">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div class="d-flex align-items-center gap-2">
          <h3 class="mb-0">{{ $t("t_customer_list") }}</h3>
          <span
            class="badge bg-success"
            style="font-size: 12px; line-height: 1.5"
            >{{ tableRules.total }} {{ $t("t_customer") }}</span
          >
        </div>
        <button
          class="btn btn-secondary btn-border"
          @click="openAddCustomerPopup"
          style="display: flex; align-items: center"
        >
          <i class="ri-add-line align-bottom me-1"></i>
          {{ $t("t-add") }}
        </button>
      </div>
      <div class="card-body">
        <div class="mb-3 d-flex align-items-center gap-2 flex-wrap">
          <el-select
            v-model="searchProfileInGroup"
            :placeholder="$t('t_profile_name')"
            clearable
            style="width: 160px"
            filterable
            @change="fn_searchCustomerInGroup"
          >
            <el-option
              v-for="p in profileOptions"
              :key="p"
              :label="p"
              :value="p"
            />
          </el-select>
          <el-select
            v-model="searchVlanInGroup"
            placeholder="VLAN"
            clearable
            style="width: 150px"
            filterable
            @change="fn_searchCustomerInGroup"
          >
            <el-option
              v-for="v in vlanOptions"
              :key="v"
              :label="v"
              :value="v"
            />
          </el-select>
          <el-select
            v-model="searchMacInGroup"
            placeholder="MAC"
            clearable
            style="width: 200px"
            filterable
            remote
            :remote-method="onMacFilterChange"
            :loading="macLoading"
            :loading-text="$t('t_loading_data')"
            @change="fn_searchCustomerInGroup"
            @clear="fetchMacsInGroup()"
          >
            <el-option v-for="m in macOptions" :key="m" :label="m" :value="m" />
          </el-select>
          <el-input
            v-model="searchCustomerInGroup"
            :placeholder="$t('t_search_customer')"
            clearable
            style="width: 220px"
            @keyup.enter="fn_searchCustomerInGroup"
          />
          <button
            class="btn btn-secondary btn-border"
            @click="fn_searchCustomerInGroup"
          >
            {{ $t("t-search") }}
          </button>
          <button
            v-if="dataTables.length > 1"
            class="btn btn-outline-danger btn-border"
            @click="handleRemoveAllCustomers"
          >
            {{ $t("t_deselect_all") }}
          </button>
        </div>

        <el-table
          border
          size="small"
          stripe
          :data="dataTables"
          style="width: 100%"
          v-loading="loading"
          :empty-text="$t('t-no-data')"
        >
          <el-table-column :label="$t('t-stt')" width="60" align="center">
            <template #default="scope">
              {{ tableRules.offset + scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column label="VLAN" width="80" align="center">
            <template #default="scope">
              {{ scope.row["vlan_id_outer"] }}
            </template>
          </el-table-column>
          <el-table-column
            label="MAC"
            min-width="150"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="">{{ scope.row["client_mac"] }}</div>
            </template>
          </el-table-column>
          <el-table-column
            label="Account"
            min-width="120"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="col-tooltip">{{ scope.row["account"] }}</div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('t_profile_name')"
            min-width="130"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="">{{ scope.row["profile_name"] }}</div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('t_profile_speed')"
            width="120"
            align="center"
          >
            <template #default="scope">
              {{ scope.row["profile_speed"] }}
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('t_olt_code')"
            min-width="150"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="">{{ scope.row["olt_code"] }}</div>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('t_bras_code')"
            min-width="150"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="">{{ scope.row["bras_code"] }}</div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('t-action')" width="80" align="center">
            <template #default="scope">
              <el-tooltip
                :content="$t('t-delete')"
                effect="light"
                placement="top"
              >
                <button
                  class="btn btn-soft-danger btn-sm"
                  @click="handleRemoveCustomer(scope.row)"
                >
                  <i class="ri-delete-bin-6-line"></i>
                </button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

        <div class="mt-3 d-flex justify-content-end">
          <el-pagination
            small
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableRules.total"
            :page-sizes="tableRules.lengthMenu"
            :page-size="tableRules.limit"
            :current-page="tableRules.page"
            @size-change="fn_tableSizeChange"
            @current-change="fn_tableCurentChange"
          />
        </div>
      </div>
    </div>

    <!-- Dialog thêm khách hàng -->
    <el-dialog
      v-model="showCustomerPopup"
      :title="$t('t_customer_list')"
      width="80%"
      class="customer-popup-dialog"
      align-center
    >
      <div class="mb-3 d-flex align-items-center gap-2 flex-wrap">
        <el-select
          v-model="searchProfileName"
          :placeholder="$t('t_profile_name')"
          clearable
          style="width: 160px"
          filterable
          @change="fn_searchPopup"
        >
          <el-option
            v-for="p in popupProfileOptions"
            :key="p"
            :label="p"
            :value="p"
          />
        </el-select>
        <el-select
          v-model="searchVlan"
          placeholder="VLAN"
          clearable
          style="width: 120px"
          filterable
          @change="fn_searchPopup"
        >
          <el-option
            v-for="v in popupVlanOptions"
            :key="v"
            :label="v"
            :value="v"
          />
        </el-select>
        <el-select
          v-model="searchMac"
          placeholder="MAC"
          clearable
          style="width: 200px"
          filterable
          remote
          :remote-method="onPopupMacFilterChange"
          :loading="popupMacLoading"
          :loading-text="$t('t_loading_data')"
          @change="fn_searchPopup"
          @clear="fetchPopupMacs()"
        >
          <el-option
            v-for="m in popupMacOptions"
            :key="m"
            :label="m"
            :value="m"
          />
        </el-select>
        <el-input
          v-model="searchAllCustomer"
          :placeholder="$t('t_search_customer')"
          clearable
          style="width: 220px"
          @keyup.enter="fn_searchPopup"
        />
        <button class="btn btn-secondary btn-border" @click="fn_searchPopup">
          {{ $t("t-search") }}
        </button>
        <button
          v-if="popupTableRules.total > 1"
          class="btn btn-soft-success btn-border"
          :disabled="isAllSelected"
          @click="selectAllCustomers"
        >
          {{ $t("t_select_all") }}
        </button>
      </div>

      <el-table
        size="small"
        max-height="450"
        stripe
        border
        :data="allCustomers"
        v-loading="popupLoading"
        :empty-text="$t('t-no-data')"
      >
        <el-table-column :label="$t('t-stt')" width="60" align="center">
          <template #default="scope">
            {{ popupTableRules.offset + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column label="VLAN" width="80" align="center">
          <template #default="scope">
            {{ scope.row["vlan_id_outer"] }}
          </template>
        </el-table-column>
        <el-table-column
          label="MAC"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div>{{ scope.row["client_mac"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          label="Account"
          min-width="120"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div>{{ scope.row["account"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_profile_name')"
          min-width="130"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div>{{ scope.row["profile_name"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_profile_speed')"
          width="120"
          align="center"
        >
          <template #default="scope">
            {{ scope.row["profile_speed"] }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_olt_code')"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div>{{ scope.row["olt_code"] }}</div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('t_bras_code')"
          min-width="150"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div>{{ scope.row["bras_code"] }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('t-action')" width="80" align="center">
          <template #default="scope">
            <el-tooltip
              :content="
                selectedCustomerIds.includes(scope.row.client_mac)
                  ? $t('t_selected')
                  : $t('t-add')
              "
              placement="top"
              effect="light"
            >
              <button
                :class="
                  selectedCustomerIds.includes(scope.row.client_mac)
                    ? 'btn btn-soft-success btn-sm'
                    : 'btn btn-soft-primary btn-sm'
                "
                @click="toggleCustomer(scope.row)"
              >
                <i
                  :class="
                    selectedCustomerIds.includes(scope.row.client_mac)
                      ? 'ri-check-line'
                      : 'ri-add-line'
                  "
                ></i>
              </button>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-3 d-flex justify-content-between align-items-center">
        <el-pagination
          small
          background
          layout="total, sizes, prev, pager, next"
          :total="popupTableRules.total"
          :page-sizes="popupTableRules.lengthMenu"
          :page-size="popupTableRules.limit"
          :current-page="popupTableRules.page"
          @size-change="fn_popupSizeChange"
          @current-change="fn_popupPageChange"
        />
        <div class="d-flex gap-2">
          <button
            class="btn btn-secondary btn-border"
            @click="confirmAddCustomers"
          >
            {{ $t("t_add_to_customer_group") }}
          </button>
        </div>
      </div>
    </el-dialog>
  </Layout>
</template>
<style lang="scss" scoped>
:deep(.el-input__wrapper) {
  height: 39px !important;
}
:deep(.el-select__wrapper) {
  height: 39px !important;
}
:deep(.el-textarea__inner) {
  min-height: 39px !important;
}
:deep(.customer-popup-dialog) {
  max-width: 1300px !important;
}
</style>
